import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/react-hooks'

import { BankCardOrderStatus, KycStatus, SpendAccount } from 'generated/types'

import CardOrderStatus from './gql/card-order-status-query.gql'

const POLLING_FREQUENCY = 2000
const POLLING_DURATION = 30000

type ResolveResult = 'success' | 'failure' | 'actionRequired' | 'timeout'
type OnResolveCardStatus = (result: ResolveResult) => void

export type CardOrderStatusQuery = {
  spendAccount: SpendAccount
}

export const hasOrderedCard = (cardOrderStatus: BankCardOrderStatus): boolean => {
  return cardOrderStatus && cardOrderStatus !== BankCardOrderStatus.NotOrdered
}

export const useCardStatusRedirect = (onRedirect = () => {}) => {
  const { cardOrderStatus, loading } = useCardStatusPolling()
  const cardOrdered = hasOrderedCard(cardOrderStatus)

  useEffect(() => {
    if (cardOrdered && !loading) {
      onRedirect()
    }
  }, [cardOrdered, loading])

  return loading
}

export const useCardStatusPolling = (onResolve: OnResolveCardStatus = () => {}) => {
  const { data, loading, startPolling: poll, stopPolling } = useQuery<CardOrderStatusQuery>(
    CardOrderStatus,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  )
  const [timer, setTimer] = useState<NodeJS.Timeout>(null)
  const accountId = data?.spendAccount.accountId
  const cardOrderStatus = data?.spendAccount.bankCardOrder?.cardOrderStatus
  const kycStatus = data?.spendAccount.bankCardOrder?.kycStatus

  const startPolling = () => {
    poll(POLLING_FREQUENCY)
  }

  useEffect(() => {
    const resolve = (result: ResolveResult) => {
      clearTimeout(timer)
      stopPolling()
      onResolve(result)
    }

    if (kycStatus === KycStatus.Pending && !timer) {
      startPolling()

      const firstTimer = setTimeout(() => {
        stopPolling()
        if (kycStatus === KycStatus.Pending) {
          resolve('timeout')
        }
      }, POLLING_DURATION)

      setTimer(firstTimer)
    }

    if (kycStatus === KycStatus.Accepted) {
      resolve('success')
    } else if (kycStatus === KycStatus.Failure) {
      resolve('failure')
    } else if (kycStatus === KycStatus.ActionRequired) {
      resolve('actionRequired')
    }
  }, [kycStatus, startPolling, stopPolling, onResolve, timer])

  return {
    accountId,
    cardOrderStatus,
    kycStatus,
    startPolling,
    loading,
  }
}
